/**
 * PAPPO — AddContact Component
 * Modal/formulário para adicionar contacto por nome de utilizador.
 */

import { useState, type FormEvent } from 'react';
import { UserPlus, X } from 'lucide-react';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import styles from './AddContact.module.css';

interface AddContactProps {
  /** Visibilidade do modal */
  isOpen: boolean;
  /** Callback para fechar */
  onClose: () => void;
  /** Callback ao submeter — chama a API e devolve o resultado */
  onAdd: (username: string) => Promise<{ success: boolean; error?: string }>;
}

export function AddContact({ isOpen, onClose, onAdd }: AddContactProps) {
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = username.trim();

    if (!trimmed) {
      setError('Insira um nome de utilizador');
      return;
    }

    setIsLoading(true);
    setError('');

    const result = await onAdd(trimmed);
    setIsLoading(false);

    if (result.success) {
      setUsername('');
      onClose();
    } else {
      setError(result.error ?? 'Erro ao adicionar contacto');
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true" aria-label="Adicionar contacto">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <h3 className={styles.title}>
            <UserPlus size={20} />
            Adicionar Contacto
          </h3>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Fechar">
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className={styles.form}>
          <Input
            label="Nome de utilizador"
            placeholder="Ex: lucas_filho"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            error={error}
            leftIcon={<UserPlus size={16} />}
            autoFocus
          />
          <div className={styles.actions}>
            <Button variant="ghost" type="button" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={isLoading}>
              Adicionar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
